/**
 * welcome-block.js - shows the ONE Welcome Block card that fits this visitor
 * (snippets/welcome-block.liquid) and runs the stranger carousel.
 *
 * Order, first that applies (design signed off 2026-09-24):
 *   member (not built yet) > signed-in customer who has ordered > host > stranger.
 *
 * The Business App decides what may show: GET /api/landing-welcome[?ref=] answers
 *   { status: 'off' }                               - nothing (the section stays as before)
 *   { status: 'ok', mode, cards: {member, customer, host, stranger},
 *     strangerCards?: [...keys], host: {...} | null } - the admin's switches
 * In test mode it answers "ok" only for a rehearsal host's link, so on the live
 * site only those visitors see anything until the admin switches it to live.
 *
 * The stranger carousel (B3d): six cards take turns every 7 seconds; the six
 * icons choose one, and choosing stops the rotation (the visitor has decided).
 * Hover or keyboard focus pauses it; it never rotates for visitors who ask their
 * device for less motion (WCAG 2.2.2).
 *
 * Pure helpers are on window.GlowmingWelcomeBlock for tools/lib/welcome-block.test.mjs.
 */
(function () {
  'use strict';

  if (typeof window !== 'undefined' && window.GlowmingWelcomeBlock) return;

  /** The design's six stranger cards, in its order. */
  /* 3 for R999 first (operator 2026-09-28); the rest in their signed-off order. */
  var STRANGER_KEYS = ['three_for_999', 'journey', 'subscribe', 'login', 'find_host', 'newsletter'];
  /** sessionStorage: the card whose sign-up form was just posted, and when. */
  var POSTED_KEY = 'glowming_wb_posted';
  /** The sign-up cards and their form ids (Shopify returns to #<id> after a post).
   *  Subscribe left this list on 1 Oct 2026: subscriptions opened and its card is now a shop link. */
  var FORM_IDS = { newsletter: 'wb-news-form' };
  /** A stored note older than this is ignored (an interrupted post). */
  var POSTED_FRESH_MS = 10 * 60 * 1000;

  /**
   * Which Welcome Block card was just signed up on, or null. The returned fragment
   * decides when there is one (another form's fragment means it was not ours); with
   * none, a note stored at submit counts only when it is fresh (Codex r1).
   */
  function postedCard(search, hash, stored, now) {
    if (!/[?&]customer_posted=true(&|$)/.test(search)) return null;
    var h = String(hash || '').replace(/^#/, '');
    var key;
    if (h) {
      for (key in FORM_IDS) if (FORM_IDS[key] === h) return key;
      return null;
    }
    try {
      var note = JSON.parse(stored || 'null');
      if (note && FORM_IDS[note.slide] && typeof note.ts === 'number' && now - note.ts >= 0 && now - note.ts < POSTED_FRESH_MS) return note.slide;
    } catch (e) { /* a corrupt note is no note */ }
    return null;
  }

  /** On the card just signed up on: the thank-you instead of the email box. */
  function markDone(root, key) {
    var form = root.querySelector('[data-slide="' + key + '"] form');
    if (!form) return;
    Array.prototype.forEach.call(form.querySelectorAll('[data-wb-field]'), function (el) { el.hidden = true; });
    var done = form.querySelector('[data-wb-done]');
    if (done) done.hidden = false;
  }
  var ROTATE_MS = 7000;

  /**
   * Which card to show: 'customer', 'host', 'stranger' or null (nothing).
   *
   * @param {object|null} body - the Business App's answer
   * @param {{hasCustomer: boolean, hasHost: boolean}} visitor
   */
  function pick(body, visitor) {
    if (!body || body.status !== 'ok' || !body.cards) return null;
    var c = body.cards;
    if (c.customer === true && visitor.hasCustomer) return 'customer';
    if (c.host === true && visitor.hasHost) return 'host';
    if (c.stranger === true && strangerKeys(body).length > 0) return 'stranger';
    return null;
  }

  /**
   * The stranger cards the admin left on, in the design's order. An answer
   * from before the admin could choose (no list) means all six.
   */
  function strangerKeys(body) {
    var list = body && Array.isArray(body.strangerCards) ? body.strangerCards : null;
    if (list === null) return STRANGER_KEYS.slice();
    return STRANGER_KEYS.filter(function (k) { return list.indexOf(k) !== -1; });
  }

  /**
   * Drop the cards that make no sense for THIS visitor: Log in for someone signed
   * in, Newsletter for someone already subscribed. Pure over the block's flags.
   *
   * @param {string[]} keys
   * @param {{hasAttribute: function(string): boolean}} block
   */
  function forVisitor(keys, block) {
    return keys.filter(function (k) {
      if (k === 'login' && block.hasAttribute('data-signed-in')) return false;
      if (k === 'newsletter' && block.hasAttribute('data-subscribed')) return false;
      return true;
    });
  }

  /**
   * The look-only stranger preview (unpublished theme only): ?wb=stranger turns it on
   * for the rest of the visit, so clicking the logo keeps it; ?wb=off turns it off.
   */
  function previewWanted(win) {
    var KEY = 'glowming_wb_preview';
    var q = win.location.search;
    try {
      if (/[?&]wb=off(?:&|$)/.test(q)) { win.sessionStorage.removeItem(KEY); return false; }
      if (/[?&]wb=stranger(?:&|$)/.test(q)) { win.sessionStorage.setItem(KEY, 'stranger'); return true; }
      return win.sessionStorage.getItem(KEY) === 'stranger';
    } catch (e) {
      return /[?&]wb=stranger(?:&|$)/.test(q);
    }
  }

  /** Replace [price] in the text nodes of an element (no HTML is parsed). */
  function fillPrice(element, price) {
    var walker = element.ownerDocument.createTreeWalker(element, 4);
    var node = walker.nextNode();
    while (node) {
      if (node.nodeValue.indexOf('[price]') !== -1) node.nodeValue = node.nodeValue.split('[price]').join(price);
      node = walker.nextNode();
    }
  }

  /** The stranger carousel: rotate, choose, pause. */
  function carousel(root, keys, doc, win, startKey, hold) {
    var slides = [];
    var tabs = [];
    Array.prototype.forEach.call(root.querySelectorAll('[data-slide]'), function (s) {
      var on = keys.indexOf(s.getAttribute('data-slide')) !== -1;
      s.hidden = !on;
      if (on) slides.push(s);
    });
    Array.prototype.forEach.call(root.querySelectorAll('[data-tab]'), function (t) {
      var on = keys.indexOf(t.getAttribute('data-tab')) !== -1;
      t.hidden = !on;
      if (on) tabs.push(t);
    });
    if (!slides.length) return;
    var reduce = win.matchMedia && win.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var cur = 0;
    var stopped = reduce || slides.length < 2;
    /* Open on a given card: her card for a host visitor (rotation carries on), or the card
       just signed up on, held so its confirmation is seen. */
    if (startKey) {
      slides.forEach(function (s, n) { if (s.getAttribute('data-slide') === startKey) { cur = n; if (hold) stopped = true; } });
    }
    root.style.setProperty('--wb-ms', ROTATE_MS + 'ms');

    function paint() {
      slides.forEach(function (s, n) { s.classList.toggle('is-on', n === cur); s.setAttribute('aria-hidden', String(n !== cur)); });
      tabs.forEach(function (t, n) {
        t.setAttribute('aria-selected', String(n === cur));
        t.tabIndex = n === cur ? 0 : -1;
        var b = t.querySelector('b');
        if (b) { b.style.animation = 'none'; void b.offsetWidth; b.style.animation = ''; }
      });
    }
    function state() { root.classList.toggle('is-running', !stopped); }
    function go(n) { cur = (n + slides.length) % slides.length; paint(); }

    tabs.forEach(function (t, n) {
      var b = t.querySelector('b');
      if (b) b.addEventListener('animationend', function () { if (!stopped && t.getAttribute('aria-selected') === 'true') go(cur + 1); });
      t.addEventListener('click', function () { stopped = true; state(); go(n); });
      t.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        stopped = true; state();
        go(cur + (e.key === 'ArrowRight' ? 1 : -1));
        tabs[cur].focus();
      });
    });
    ['mouseenter', 'focusin'].forEach(function (ev) { root.addEventListener(ev, function () { root.classList.add('is-paused'); }); });
    ['mouseleave', 'focusout'].forEach(function (ev) { root.addEventListener(ev, function () { root.classList.remove('is-paused'); }); });
    paint();
    state();
  }

  /**
   * "Find a host": when the finder in the card records a chosen host, show the
   * compact host card instead of the search (and the other way round on Change).
   * The label is the finder's own public "First L" form.
   */
  function bindFinderCard(root) {
    var box = root.querySelector('[data-slide="find_host"] [data-cart-finder]');
    var card = root.querySelector('[data-wb-finder-chosen]');
    var intro = root.querySelector('[data-wb-finder-intro]');
    var art = root.querySelector('[data-wb-finder-art]');
    /* Attached to a host: the "Find a host" eyebrow goes and her card takes the top spot. */
    var eyebrow = root.querySelector('[data-wb-finder-ey]');
    if (!box || !card) return;
    function sync() {
      var chosen = box.getAttribute('data-state') === 'chosen';
      var label = (box.getAttribute('data-chosen-label') || '').trim();
      if (chosen && label) {
        var parts = label.split(/\s+/);
        var first = parts[0];
        var initial = parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
        card.querySelector('[data-wb-fc-initials]').textContent = first.charAt(0).toUpperCase() + initial;
        card.querySelector('[data-wb-fc-name]').textContent = initial ? first + ' ' + initial + '.' : first;
        var t = (card.getAttribute('data-label-shop') || '').trim() || 'Shop with {name}';
        card.querySelector('[data-wb-fc-shop]').textContent = t.split('{name}').join(first);
        drawSocials((box.getAttribute('data-chosen-ref') || '').trim(), first);
      } else {
        drawSocials('', '');
      }
      card.hidden = !(chosen && label);
      if (intro) intro.hidden = chosen && !!label;
      if (art) art.hidden = chosen && !!label;
      if (eyebrow) eyebrow.hidden = chosen && !!label;
    }
    var soc = card.querySelector('[data-wb-fc-socials]');
    var endpoint = (root.closest('[data-welcome-block]') || root).getAttribute('data-endpoint');
    var socialsFor = null;
    /* Her socials come from the Business App's public card for that ref (the finder
       search carries only name and ref). A late answer for a previous pick is ignored. */
    function drawSocials(ref, first) {
      if (!soc || ref === socialsFor) return;
      socialsFor = ref;
      var row = soc.querySelector('[data-wb-fc-socials-row]');
      row.textContent = '';
      soc.hidden = true;
      var quote = card.querySelector('[data-wb-fc-quote]');
      if (quote) { quote.textContent = ''; quote.hidden = true; }
      var H = window.GlowmingHomeHost;
      if (!ref || !endpoint || !H || !H.ICONS) return;
      fetch(endpoint + '?ref=' + encodeURIComponent(ref), { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; })
        .then(function (body) {
          if (socialsFor !== ref) return;
          var host = H.cardFrom(body);
          if (!host) return;
          /* Her welcome as the quote; a headline with no message stands in for it (as the host card). */
          var words = host.message || host.headline;
          if (quote && words) { quote.textContent = '\u201C' + words + '\u201D'; quote.hidden = false; }
          if (!host.socials.length) return;
          soc.querySelector('[data-wb-fc-socials-label]').textContent = H.label(soc.getAttribute('data-label'), first, 'Find {name} on');
          host.socials.forEach(function (s) {
            var a = root.ownerDocument.createElement('a');
            a.className = 'home-host__soc-a';
            a.href = s.url;
            a.target = '_blank';
            a.rel = 'noopener';
            a.title = s.name;
            a.setAttribute('aria-label', first + ' on ' + s.name);
            a.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' + H.ICONS[s.key] + '</svg>';
            row.appendChild(a);
          });
          soc.hidden = false;
        })
        .catch(function () { /* no socials row */ });
    }
    var change = card.querySelector('[data-wb-fc-change]');
    if (change) change.addEventListener('click', function () {
      var own = box.querySelector('[data-finder-change]');
      if (own) own.click();
    });
    if (typeof MutationObserver !== 'undefined') new MutationObserver(sync).observe(box, { attributes: true, attributeFilter: ['data-state', 'data-chosen-label', 'data-chosen-ref'] });
    sync();
  }

  /** The customer's one-tap repeat: add the same pouches, then open the cart. */
  function bindRepeat(root, win) {
    var btn = root.querySelector('[data-wb-repeat]');
    if (!btn) return;
    var items = [];
    try { items = JSON.parse(btn.getAttribute('data-items') || '[]'); } catch (e) { items = []; }
    items = items.filter(function (it) { return it && typeof it.id === 'number' && typeof it.quantity === 'number' && it.quantity > 0; });
    if (!items.length) { btn.hidden = true; return; }
    /* A failed add used to reach ONLY the hidden screen-reader region, so on
       screen the button looked dead (operator, 2026-10-07: "The repeat my order
       button does not work"). The reason now shows in the note under the
       button, and the shop's status goes to the console for diagnosis. */
    var slide = btn.closest('[data-slide]') || root;
    var note = slide.querySelector('.wb-note');
    var label = btn.textContent;
    btn.addEventListener('click', function () {
      if (!win.GlowmingCart) { win.location.href = '/cart'; return; }
      btn.disabled = true;
      btn.textContent = 'Adding…';
      win.GlowmingCart.add({ items: items })
        .then(function () { win.location.href = '/cart'; })
        .catch(function (err) {
          var msg = err && err.message ? err.message : 'Could not add';
          if (win.console && win.console.warn) win.console.warn('[welcome-block] repeat order failed', err && err.status, msg, items);
          win.GlowmingCart.announce(msg);
          if (note) { note.textContent = msg + ' Please try again, or add the pouches from the shop.'; note.classList.add('wb-note--error'); }
          btn.textContent = label;
          btn.disabled = false;
        });
    });
  }

  /** Show one card and switch the section to its Welcome Block layout. */
  function show(block) {
    var card = block.querySelector('[data-wb-stranger]');
    if (card) card.hidden = false;
    var section = block.closest('.what');
    if (section) section.classList.add('what--hosted');
  }

  /**
   * The Business App's answer for this visitor, or null. A saved ref the Business
   * App does not know (an old or foreign code, answered 400 or 404) must not blank
   * the card: the answer is asked for again WITHOUT the ref, so the visitor still
   * gets the card the switch allows. The saved ref itself is left alone. Before
   * this, every such visitor saw an empty right column (Anton, 2026-09-28).
   *
   * @param {Function} fetchFn - window.fetch, injectable for the test
   * @param {string} endpoint - GET /api/landing-welcome
   * @param {string|null} ref - the saved affiliate ref, if any
   */
  function loadWelcome(fetchFn, endpoint, ref) {
    var ask = function (withRef) {
      return fetchFn(endpoint + (withRef ? '?ref=' + encodeURIComponent(withRef) : ''), { mode: 'cors', credentials: 'omit', headers: { Accept: 'application/json' } })
        .then(function (res) { return res.ok ? res.json() : null; });
    };
    return ask(ref).then(function (body) { return body === null && ref ? ask(null) : body; });
  }

  function start() {
    var doc = document;
    var win = window;
    var block = doc.querySelector('[data-welcome-block]');
    if (!block) return;
    var H = win.GlowmingHomeHost;
    /* Look-only preview on an unpublished theme: ?wb=stranger (see welcome-block.liquid).
       The live theme never renders data-preview-allowed, so the parameter does nothing there. */
    if (block.getAttribute('data-preview-allowed') === 'unpublished' && previewWanted(win)) {
      startStranger(block, STRANGER_KEYS.slice(), doc, win);
      return;
    }
    var stored = null;
    try { stored = win.localStorage.getItem('glowming_affiliate_ref'); } catch (e) { stored = null; }
    var ref = H ? H.knownRef(stored, doc.cookie, block.getAttribute('data-cart-ref')) || (H.refFromSearch ? H.refFromSearch(win.location.search) : null) : null;
    loadWelcome(win.fetch.bind(win), block.getAttribute('data-endpoint'), ref)
      .then(function (body) {
        var hostCard = H && body ? H.cardFrom(body) : null;
        var which = pick(body, { hasCustomer: block.hasAttribute('data-has-customer'), hasHost: !!hostCard });
        /* A host visitor: the carousel, opening on her card (Find a host shows the chosen host),
           so the Glowming Journey and the offers are one tap away (operator 2026-09-25). */
        if (which === 'host') {
          var hk = strangerKeys(body);
          startStranger(block, forVisitor(STRANGER_KEYS.filter(function (k) { return k === 'find_host' || hk.indexOf(k) !== -1; }), block), doc, win, 'find_host');
          return;
        }
        /* A signed-in customer: her card first, then the other cards rotate (operator 2026-09-28). */
        if (which === 'customer') { bindRepeat(block, win); startStranger(block, forVisitor(['customer'].concat(strangerKeys(body)), block), doc, win, 'customer'); return; }
        if (which === 'stranger') startStranger(block, forVisitor(strangerKeys(body), block), doc, win);
      })
      .catch(function () { /* the section stays as it was */ });
  }

  /**
   * The stranger card. The promotion card holds no price: [price] comes from the
   * offers endpoint, and it is resolved BEFORE the carousel starts, so a promotion
   * that is not running is never in the rotation (Codex r1, High). No cards left:
   * nothing shows.
   */
  function startStranger(block, keys, doc, win, openOn) {
    var root = block.querySelector('[data-wb-stranger]');
    if (!root) return;
    /* A sign-up form reloads the page; note which card it was on, and when. */
    root.addEventListener('submit', function (e) {
      var s = e.target && e.target.closest ? e.target.closest('[data-slide]') : null;
      var k = s ? s.getAttribute('data-slide') : null;
      try { if (k && FORM_IDS[k]) win.sessionStorage.setItem(POSTED_KEY, JSON.stringify({ slide: k, ts: Date.now() })); } catch (err) { /* no storage: the fragment still works */ }
    });
    var stored = null;
    try { stored = win.sessionStorage.getItem(POSTED_KEY); win.sessionStorage.removeItem(POSTED_KEY); } catch (err) { stored = null; }
    var posted = postedCard(win.location.search, win.location.hash, stored, Date.now());
    if (posted && root.querySelector('[data-slide="' + posted + '"]')) {
      markDone(root, posted);
      /* Always in the rotation, even if the admin has since turned that card off (Codex r1). */
      /* Appended, not rebuilt from STRANGER_KEYS: that dropped the customer's own card (Codex r2,
         2026-09-28). The carousel takes its order from the DOM, so the position here does not matter. */
      if (keys.indexOf(posted) === -1) keys = keys.concat(posted);
    } else {
      posted = null;
    }
    var begin = function (finalKeys) {
      if (!finalKeys.length) return;
      if (posted && finalKeys.indexOf(posted) !== -1) carousel(root, finalKeys, doc, win, posted, true);
      else carousel(root, finalKeys, doc, win, openOn && finalKeys.indexOf(openOn) !== -1 ? openOn : null, false);
      if (posted && block.scrollIntoView) block.scrollIntoView({ block: 'center' });
      bindFinderCard(root);
      show(block);
    };
    var slide = root.querySelector('[data-wb-offer]');
    if (keys.indexOf('three_for_999') === -1 || !slide) { begin(keys); return; }
    var without = keys.filter(function (k) { return k !== 'three_for_999'; });
    var key = slide.getAttribute('data-wb-offer');
    var O = win.GlowmingOffers;
    var endpoint = block.getAttribute('data-offers-endpoint');
    if (!key || !O || !endpoint) { begin(without); return; }
    var apply = function (payload) {
      var price = priceFor(payload, key);
      if (!price) { begin(without); return; }
      Array.prototype.forEach.call(slide.querySelectorAll('[data-wb-price-text]'), function (el) { fillPrice(el, price); });
      begin(keys);
    };
    var cached = O.readCache(win.sessionStorage, Date.now());
    if (cached) { apply(cached); return; }
    O.fetchOffers(endpoint).then(function (payload) {
      if (payload) O.writeCache(win.sessionStorage, Date.now(), payload);
      apply(payload);
    }, function () { begin(without); });
  }

  /** "R999" for a running ZAR offer with this key, else null. */
  function priceFor(payload, key) {
    if (!payload || !Array.isArray(payload.offers)) return null;
    var offer = payload.offers.filter(function (o) { return o && o.key === key; })[0];
    if (!offer || offer.currency !== 'ZAR' || typeof offer.amount !== 'number' || !isFinite(offer.amount) || offer.amount < 0) return null;
    var whole = Math.round(offer.amount * 100) % 100 === 0;
    var parts = (whole ? String(Math.round(offer.amount)) : offer.amount.toFixed(2)).split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return 'R' + parts.join('.');
  }

  window.GlowmingWelcomeBlock = { STRANGER_KEYS: STRANGER_KEYS, pick: pick, forVisitor: forVisitor, loadWelcome: loadWelcome, strangerKeys: strangerKeys, priceFor: priceFor, startStranger: startStranger, postedCard: postedCard, FORM_IDS: FORM_IDS };

  if (typeof document !== 'undefined' && document.querySelector) {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
    else start();
  }
})();
